import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { Link } from '@dndbeyond/character-common-components/es';
import {
    AbilitySummary,
    CampaignSummary,
    DarkChatBubbleSvg,
    DarkPlayButtonSvg,
    FeatureFlagContext,
    LightChatBubbleSvg,
    LightPlayButtonSvg,
    ManageIcon,
} from '@dndbeyond/character-components/es';
import {
    Ability,
    AbilityUtils,
    CampaignDataContract,
    CharacterPreferences,
    CharacterTheme,
    DeathSaveInfo,
    Modifier,
    ProficiencyGroup,
    RuleData,
    rulesEngineSelectors,
    SenseInfo,
    SituationalSavingThrowInfoLookup,
    Skill,
    SkillUtils,
    ValueLookup,
    DiceAdjustment,
} from '@dndbeyond/character-rules-engine/es';
import { IRollContext } from '@dndbeyond/dice';
import { GameLogNotificationWrapper } from '@dndbeyond/game-log-components';

import { sidebarActions } from '../../../../Shared/actions';
import { appEnvSelectors, characterRollContextSelectors } from '../../../../Shared/selectors';
import { AppEnvDimensionsState, GameLogState } from '../../../../Shared/stores/typings';
import { PaneComponentEnum, PaneIdentifierUtils } from '../../../../Shared/utils';
import { NavigationUtils } from '../../../../Shared/utils';
import ProficiencyGroupsBox from '../../../components/ProficiencyGroupsBox';
import SavingThrowsBox from '../../../components/SavingThrowsBox';
import SensesBox from '../../../components/SensesBox';
import SkillsBox from '../../../components/SkillsBox';
import SubsectionTablet from '../../../components/SubsectionTablet';
import { SheetAppState } from '../../../typings';

interface Props extends DispatchProp {
    abilities: Array<Ability>;
    preferences: CharacterPreferences;
    savingThrowDiceAdjustments: Array<DiceAdjustment>;
    situationalBonusSavingThrowsLookup: SituationalSavingThrowInfoLookup;
    deathSaveInfo: DeathSaveInfo;
    passiveInsight: number;
    passiveInvestigation: number;
    passivePerception: number;
    senses: SenseInfo;
    campaign: CampaignDataContract | null;
    skills: Array<Skill>;
    valueLookup: ValueLookup;
    customSkills: Array<Skill>;
    proficiencyGroups: Array<ProficiencyGroup>;
    dimensions: AppEnvDimensionsState;
    theme: CharacterTheme;
    isReadonly: boolean;
    diceEnabled: boolean;
    ruleData: RuleData;
    characterRollContext: IRollContext;
    gameLog: GameLogState;
}
class MainTablet extends React.PureComponent<Props> {
    static defaultProps = {
        diceEnabled: false,
    };

    handleSensesManageShow = (): void => {
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SENSE_MANAGE));
    };

    handleSavingThrowsClick = (): void => {
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SAVING_THROWS));
    };

    handleAbilitySummaryClick = (ability: Ability): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.ABILITY,
                PaneIdentifierUtils.generateAbility(AbilityUtils.getId(ability)),
            ),
        );
    };

    handleAbilityClick = (ability: Ability): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.ABILITY_SAVING_THROW,
                PaneIdentifierUtils.generateAbilitySavingThrows(AbilityUtils.getId(ability)),
            ),
        );
    };

    handleProficienciesManageShow = (): void => {
        const { dispatch, isReadonly } = this.props;

        if (!isReadonly) {
            dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.PROFICIENCIES));
        }
    };

    handleCampaignShow = (): void => {
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.CAMPAIGN));
    };

    handleGameLogClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;

        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.GAME_LOG));
    };

    handleCustomSkillClick = (skill: Skill): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.CUSTOM_SKILL,
                PaneIdentifierUtils.generateCustomSkill(SkillUtils.getId(skill)),
            ),
        );
    };

    handleSkillsShow = (): void => {
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SKILLS));
    };

    handleSkillClick = (skill: Skill): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.SKILL,
                PaneIdentifierUtils.generateSkill(SkillUtils.getId(skill)),
            ),
        );
    };

    render() {
        const {
            preferences,
            abilities,
            savingThrowDiceAdjustments,
            situationalBonusSavingThrowsLookup,
            deathSaveInfo,
            passiveInsight,
            passiveInvestigation,
            passivePerception,
            senses,
            campaign,
            skills,
            valueLookup,
            customSkills,
            proficiencyGroups,
            theme,
            dimensions,
            ruleData,
            isReadonly,
            diceEnabled,
            characterRollContext,
            gameLog,
        } = this.props;

        return (
            <SubsectionTablet name="Main">
                <div className="ct-main-tablet">
                    {campaign && (
                        <>
                            <div
                                className={`ct-main-tablet__campaign ${
                                    theme.isDarkMode ? 'ct-main-tablet__campaign--dark-mode' : ''
                                }`}
                            >
                                <CampaignSummary
                                    campaign={campaign}
                                    onCampaignShow={this.handleCampaignShow}
                                    className={theme.isDarkMode ? 'ct-main-tablet__campaign-button--dark-mode' : ''}
                                />
                                <FeatureFlagContext.Consumer>
                                    {(ffm) =>
                                        ffm.getFlag(ffm.FLAGS.RELEASE_GATE_TACTICAL_MAPS) && (
                                            <Link url={NavigationUtils.getLaunchGameUrl(campaign)} target="maps">
                                                <div className="ct-character-header__group--game-log">
                                                    <div className="ct-character-header-desktop__button-icon">
                                                        {theme.isDarkMode ? (
                                                            <LightPlayButtonSvg />
                                                        ) : (
                                                            <DarkPlayButtonSvg />
                                                        )}
                                                    </div>
                                                </div>
                                            </Link>
                                        )
                                    }
                                </FeatureFlagContext.Consumer>
                                <GameLogNotificationWrapper
                                    themeColor={theme.themeColor}
                                    gameLogIsOpen={gameLog.isOpen}
                                    notificationOnClick={this.handleGameLogClick}
                                >
                                    <div
                                        className="ct-character-header__group--game-log ct-character-header__group--game-log-last"
                                        onClick={this.handleGameLogClick}
                                    >
                                        <div className="ct-character-header-desktop__button-icon">
                                            {theme.isDarkMode ? <LightChatBubbleSvg /> : <DarkChatBubbleSvg />}
                                        </div>
                                    </div>
                                </GameLogNotificationWrapper>
                            </div>
                        </>
                    )}
                    <div className="ct-main-tablet__abilities">
                        {abilities.map((ability) => (
                            <div className="ct-main-tablet__ability" key={ability.statKey}>
                                <AbilitySummary
                                    theme={theme}
                                    ability={ability}
                                    preferences={preferences}
                                    onClick={this.handleAbilitySummaryClick}
                                    diceEnabled={diceEnabled}
                                    rollContext={characterRollContext}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="ct-main-tablet__large-boxes">
                        <div className="ct-main-tablet__large-boxes-bucket">
                            <div className="ct-main-tablet__large-box">
                                <SavingThrowsBox
                                    abilities={abilities}
                                    ruleData={ruleData}
                                    savingThrowDiceAdjustments={savingThrowDiceAdjustments}
                                    situationalBonusSavingThrowsLookup={situationalBonusSavingThrowsLookup}
                                    deathSaveInfo={deathSaveInfo}
                                    onAbilityClick={this.handleAbilityClick}
                                    onClick={this.handleSavingThrowsClick}
                                    dimensions={dimensions}
                                    theme={theme}
                                    diceEnabled={diceEnabled}
                                    rollContext={characterRollContext}
                                />
                                <div className="ct-main-tablet__large-box-footer">
                                    <ManageIcon
                                        onClick={this.handleSavingThrowsClick}
                                        enableTooltip={false}
                                        theme={theme}
                                    >
                                        Saving Throws
                                    </ManageIcon>
                                </div>
                            </div>
                            <div className="ct-main-tablet__large-box">
                                <SensesBox
                                    senses={senses}
                                    passiveInsight={passiveInsight}
                                    passiveInvestigation={passiveInvestigation}
                                    passivePerception={passivePerception}
                                    onClick={this.handleSensesManageShow}
                                    dimensions={dimensions}
                                    theme={theme}
                                />
                                <div className="ct-main-tablet__large-box-footer">
                                    <ManageIcon
                                        onClick={this.handleSensesManageShow}
                                        enableTooltip={false}
                                        theme={theme}
                                    >
                                        Senses
                                    </ManageIcon>
                                </div>
                            </div>
                            <div className="ct-main-tablet__large-box">
                                <ProficiencyGroupsBox
                                    dimensions={dimensions}
                                    theme={theme}
                                    proficiencyGroups={proficiencyGroups}
                                    onClick={this.handleProficienciesManageShow}
                                />
                                <div className="ct-main-tablet__large-box-footer">
                                    <ManageIcon
                                        onClick={this.handleProficienciesManageShow}
                                        enableTooltip={false}
                                        showIcon={!isReadonly}
                                        theme={theme}
                                    >
                                        Proficiencies &amp; Languages
                                    </ManageIcon>
                                </div>
                            </div>
                        </div>
                        <div className="ct-main-tablet__large-boxes-bucket">
                            <div className="ct-main-tablet__large-box">
                                <SkillsBox
                                    skills={skills}
                                    customSkills={customSkills}
                                    valueLookup={valueLookup}
                                    onCustomSkillClick={this.handleCustomSkillClick}
                                    onSkillClick={this.handleSkillClick}
                                    onEmptyClick={this.handleSkillsShow}
                                    dimensions={dimensions}
                                    theme={theme}
                                    diceEnabled={diceEnabled}
                                    ruleData={ruleData}
                                    rollContext={characterRollContext}
                                />
                                <div className="ct-main-tablet__large-box-footer">
                                    <ManageIcon onClick={this.handleSkillsShow} enableTooltip={false} theme={theme}>
                                        Skills
                                    </ManageIcon>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </SubsectionTablet>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        abilities: rulesEngineSelectors.getAbilities(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        savingThrowDiceAdjustments: rulesEngineSelectors.getSavingThrowDiceAdjustments(state),
        situationalBonusSavingThrowsLookup: rulesEngineSelectors.getSituationalBonusSavingThrowsLookup(state),
        deathSaveInfo: rulesEngineSelectors.getDeathSaveInfo(state),
        passivePerception: rulesEngineSelectors.getPassivePerception(state),
        passiveInvestigation: rulesEngineSelectors.getPassiveInvestigation(state),
        passiveInsight: rulesEngineSelectors.getPassiveInsight(state),
        senses: rulesEngineSelectors.getSenseInfo(state),
        campaign: rulesEngineSelectors.getCampaign(state),
        skills: rulesEngineSelectors.getSkills(state),
        valueLookup: rulesEngineSelectors.getCharacterValueLookup(state),
        customSkills: rulesEngineSelectors.getCustomSkills(state),
        proficiencyGroups: rulesEngineSelectors.getProficiencyGroups(state),
        dimensions: appEnvSelectors.getDimensions(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        characterRollContext: characterRollContextSelectors.getCharacterRollContext(state),
        gameLog: appEnvSelectors.getGameLog(state),
    };
}

export default connect(mapStateToProps)(MainTablet);
